import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Trash2,
  RefreshCw,
  Copy,
  Inbox,
  TrendingUp,
  Tag,
  Download,
} from "lucide-react";
import {
  deleteLeadById,
  fetchLeads,
  isSupabaseConfigured,
  verifyAdminPasscode,
} from "../lib/supabaseApi";
import { setPageSeo } from "../lib/seo";

const STORAGE_KEY = "dzc_admin_passcode";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function Login({ onAuthed }) {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    if (!isSupabaseConfigured) {
      toast.error("Supabase is not configured.");
      return;
    }
    setLoading(true);
    try {
      const valid = await verifyAdminPasscode(passcode.trim());
      if (!valid) {
        throw new Error("Could not verify passcode. Try again.");
      }
      localStorage.setItem(STORAGE_KEY, passcode.trim());
      toast.success("Welcome, Ishu.");
      onAuthed(passcode.trim());
    } catch (err) {
      const msg = err?.message || "Could not verify passcode. Try again.";
      toast.error(typeof msg === "string" ? msg : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-ink-0 text-white flex items-center justify-center px-6"
      data-testid="admin-login"
    >
      <div className="absolute inset-0 tech-grid opacity-30 pointer-events-none" />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-ink-50 border border-white/10 rounded-2xl p-8 md:p-10"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl border border-white/20 bg-white/[0.03] p-2 flex items-center justify-center">
            <img src="/logo.svg" alt="Devzcorner logo" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Devzcorner · Admin
            </div>
            <h1 className="font-display text-2xl mt-1">Enter passcode</h1>
          </div>
        </div>
        <p className="text-sm text-zinc-400 mb-6">
          This console is restricted. Enter the admin passcode to view
          submitted project requests.
        </p>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            Passcode
          </span>
          <input
            type="password"
            autoFocus
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="••••••••••••"
            data-testid="admin-passcode-input"
            className="mt-2 w-full bg-transparent border-b border-white/10 text-white py-3 placeholder-zinc-600 outline-none focus:border-accent-cyan transition-colors"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-8 w-full px-5 py-3 rounded-full bg-white text-black text-sm font-semibold hover:bg-zinc-200 transition-all hover:scale-[1.01] disabled:opacity-60"
        >
          {loading ? "Verifying…" : "Unlock"}
        </button>
        <a
          href="/"
          className="mt-5 block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-white"
        >
          ← Back to site
        </a>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tint = "cyan" }) {
  const tintMap = {
    cyan: "border-accent-cyan/30 text-accent-cyan bg-accent-cyan/5",
    purple: "border-accent-purple/30 text-accent-purple bg-accent-purple/5",
    white: "border-white/10 text-white bg-white/5",
  };
  return (
    <div className="bg-ink-50 border border-white/10 rounded-2xl p-5 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {label}
        </span>
        <div
          className={`w-9 h-9 rounded-lg border flex items-center justify-center ${tintMap[tint]}`}
        >
          <Icon size={15} strokeWidth={1.8} />
        </div>
      </div>
      <div className="font-display text-3xl md:text-4xl">{value}</div>
    </div>
  );
}

function Dashboard({ passcode, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, last_7d: 0, by_type: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const buildStats = (rows) => {
    const last7Cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const last7d = rows.filter((l) => {
      const t = new Date(l.created_at).getTime();
      return Number.isFinite(t) && t >= last7Cutoff;
    }).length;

    const counts = rows.reduce((acc, lead) => {
      const key = lead.project_type || "Unspecified";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const by_type = Object.entries(counts)
      .map(([project_type, count]) => ({ project_type, count }))
      .sort((a, b) => b.count - a.count);

    return { total: rows.length, last_7d: last7d, by_type };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchLeads();
      setLeads(rows);
      setStats(buildStats(rows));
    } catch (err) {
      if (err?.message?.toLowerCase().includes("configured")) {
        toast.error("Supabase is not configured.");
      } else {
        toast.error("Failed to load leads.");
      }
      if (!verifyAdminPasscode(passcode)) {
        toast.error("Session expired. Please log in again.");
        onLogout();
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passcode]);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await deleteLeadById(id);
      toast.success("Lead deleted.");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err?.message || "Delete failed.");
    }
  };

  const onCopyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`Copied ${email}`);
    } catch {
      toast.error("Could not copy.");
    }
  };

  const filtered = leads.filter((l) => {
    const createdAt = new Date(l.created_at).getTime();
    const now = Date.now();
    const timeOk =
      timeFilter === "all"
        ? true
        : timeFilter === "7d"
        ? Number.isFinite(createdAt) && createdAt >= now - 7 * 24 * 60 * 60 * 1000
        : timeFilter === "30d"
        ? Number.isFinite(createdAt) && createdAt >= now - 30 * 24 * 60 * 60 * 1000
        : true;

    const typeOk =
      typeFilter === "all" ? true : (l.project_type || "Unspecified") === typeFilter;

    if (!filter.trim()) return timeOk && typeOk;
    const q = filter.toLowerCase();
    const searchOk = (
      l.name?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.project_type?.toLowerCase().includes(q) ||
      l.message?.toLowerCase().includes(q)
    );
    return searchOk && timeOk && typeOk;
  });

  const projectTypes = [
    "all",
    ...Array.from(
      new Set(leads.map((l) => l.project_type || "Unspecified").filter(Boolean))
    ),
  ];

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("No leads to export for current filters.");
      return;
    }
    const headers = ["Date", "Name", "Email", "Type", "Message"];
    const rows = filtered.map((lead) => [
      formatDate(lead.created_at),
      lead.name || "",
      lead.email || "",
      lead.project_type || "Unspecified",
      (lead.message || "").replace(/\n/g, " ").trim(),
    ]);

    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `devzcorner-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Leads exported.");
  };

  return (
    <div
      className="min-h-screen bg-ink-0 text-white"
      data-testid="admin-dashboard"
    >
      <div className="absolute inset-0 tech-grid opacity-20 pointer-events-none" />

      {/* Top bar */}
      <header className="relative border-b border-white/5 bg-ink-0/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md border border-white/20 bg-white/[0.03] p-1.5 flex items-center justify-center">
              <img src="/logo.svg" alt="Devzcorner logo" className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-base">Devzcorner</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Admin · Leads
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCsv}
              data-testid="admin-export"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 text-xs text-zinc-300 hover:text-white hover:border-white/30 transition-all"
            >
              <Download size={13} />
              Export
            </button>
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 text-xs text-zinc-300 hover:text-white hover:border-white/30 transition-all"
            >
              <RefreshCw size={13} />
              Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 text-xs text-zinc-300 hover:text-white hover:border-white/30 transition-all"
            >
              <LogOut size={13} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="relative max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          <StatCard icon={Inbox} label="Total Leads" value={stats.total} tint="white" />
          <StatCard
            icon={TrendingUp}
            label="Last 7 Days"
            value={stats.last_7d}
            tint="cyan"
          />
          <StatCard
            icon={Tag}
            label="Top Type"
            value={stats.by_type?.[0]?.project_type || "—"}
            tint="purple"
          />
        </div>

        {/* Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search name, email, type, or message…"
            data-testid="admin-search"
            className="flex-1 bg-ink-50 border border-white/10 rounded-full px-5 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-white/30 transition-colors"
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-ink-50 border border-white/10 rounded-full px-4 py-3 text-sm text-zinc-300 outline-none focus:border-white/30 transition-colors"
          >
            {projectTypes.map((type) => (
              <option key={type} value={type} className="bg-ink-50 text-zinc-200">
                {type === "all" ? "All Types" : type}
              </option>
            ))}
          </select>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="bg-ink-50 border border-white/10 rounded-full px-4 py-3 text-sm text-zinc-300 outline-none focus:border-white/30 transition-colors"
          >
            <option value="all" className="bg-ink-50 text-zinc-200">All Time</option>
            <option value="7d" className="bg-ink-50 text-zinc-200">Last 7 Days</option>
            <option value="30d" className="bg-ink-50 text-zinc-200">Last 30 Days</option>
          </select>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center">
            {filtered.length} / {leads.length} shown
          </div>
        </div>

        {/* Table */}
        <div
          className="bg-ink-50 border border-white/10 rounded-2xl overflow-hidden"
          data-testid="admin-leads-table"
        >
          {loading ? (
            <div className="p-16 text-center text-zinc-500 font-mono text-xs uppercase tracking-[0.3em]">
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <Inbox className="mx-auto text-zinc-600 mb-4" size={32} />
              <div className="font-display text-xl mb-1">
                {leads.length === 0 ? "No leads yet" : "No matches"}
              </div>
              <div className="text-sm text-zinc-500">
                {leads.length === 0
                  ? "Submissions from the landing page will appear here."
                  : "Try a different search term."}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-b border-white/5">
                    <th className="px-5 py-4 font-medium">Date</th>
                    <th className="px-5 py-4 font-medium">Name</th>
                    <th className="px-5 py-4 font-medium">Email</th>
                    <th className="px-5 py-4 font-medium">Type</th>
                    <th className="px-5 py-4 font-medium">Message</th>
                    <th className="px-5 py-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead) => (
                    <tr
                      key={lead.id}
                      onClick={() => setSelected(lead)}
                      data-testid={`admin-lead-row-${lead.id}`}
                      className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-4 font-mono text-[11px] text-zinc-500 whitespace-nowrap">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="px-5 py-4 text-white">{lead.name}</td>
                      <td className="px-5 py-4 text-zinc-300">{lead.email}</td>
                      <td className="px-5 py-4">
                        {lead.project_type ? (
                          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 border border-white/10 rounded-full px-2 py-0.5">
                            {lead.project_type}
                          </span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-zinc-400 max-w-md truncate">
                        {lead.message}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onCopyEmail(lead.email);
                            }}
                            aria-label="Copy email"
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-all"
                          >
                            <Copy size={12} />
                          </button>
                          <a
                            href={`mailto:${lead.email}?subject=Re:%20your%20Devzcorner%20project%20request`}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Email"
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-all"
                          >
                            <Mail size={12} />
                          </a>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(lead.id);
                            }}
                            aria-label="Delete"
                            data-testid={`admin-lead-delete-${lead.id}`}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-500/40 transition-all"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex"
          onClick={() => setSelected(null)}
        >
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-ink-50 border-l border-white/10 h-full overflow-y-auto"
            data-testid="admin-lead-drawer"
          >
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                    {formatDate(selected.created_at)}
                  </div>
                  <h2 className="font-display text-2xl">{selected.name}</h2>
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-sm text-accent-cyan hover:underline"
                  >
                    {selected.email}
                  </a>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white"
                >
                  Close
                </button>
              </div>
              {selected.project_type && (
                <div className="mb-6">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 border border-white/10 rounded-full px-3 py-1.5">
                    {selected.project_type}
                  </span>
                </div>
              )}
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                Message
              </div>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">
                {selected.message}
              </p>
              <div className="mt-8 flex gap-2">
                <a
                  href={`mailto:${selected.email}?subject=Re:%20your%20Devzcorner%20project%20request`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-black text-sm font-semibold"
                >
                  <Mail size={14} /> Reply
                </a>
                <button
                  onClick={() => onDelete(selected.id)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-sm text-zinc-300 hover:text-red-400 hover:border-red-500/40 transition-all"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [passcode, setPasscode] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) || "" : ""
  );

  // Verify cached passcode on mount
  useEffect(() => {
    setPageSeo({
      title: "Restricted Console — Devzcorner",
      description: "Restricted admin console.",
      path: window.location.pathname || "/",
      noindex: true,
    });
    if (!passcode) return;
    (async () => {
      const valid = await verifyAdminPasscode(passcode);
      if (!valid) {
        localStorage.removeItem(STORAGE_KEY);
        setPasscode("");
      }
    })();
  }, [passcode]);

  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPasscode("");
    toast.success("Signed out.");
  };

  if (!passcode) {
    return <Login onAuthed={setPasscode} />;
  }
  return <Dashboard passcode={passcode} onLogout={onLogout} />;
}
